<template>
  <div
    class="cycle-task-dependency-section"
    data-testid="cycle-task-dependencies"
  >
    <button
      type="button"
      class="cycle-task-dependency-section__header"
      :aria-expanded="expanded ? 'true' : 'false'"
      data-testid="cycle-task-dependencies__header"
      @click="toggleExpanded"
    >
      <span class="cycle-task-dependency-section__header-left">
        <span
          class="cycle-task-dependency-section__chevron"
          :class="{ 'cycle-task-dependency-section__chevron--expanded': expanded }"
        >
          <img
            src="/img/icons/fbc-icons/AccordionChevron.svg"
            alt=""
            class="cycle-task-dependency-section__chevron-icon"
          />
        </span>
        <span class="cycle-task-dependency-section__title">Dependencies</span>
      </span>
      <span class="cycle-task-dependency-section__header-right">
        <span
          v-if="!subjectDueReady"
          class="cycle-task-dependency-section__due-hint"
          data-testid="cycle-task-dependencies__due-hint"
        >
          {{ copy.PICKER_DUE_REQUIRED }}
        </span>
        <span class="cycle-task-dependency-section__summary">
          {{ summaryLabel || copy.EMPTY_DIALOG_SUMMARY }}
        </span>
      </span>
    </button>

    <div v-show="expanded" class="cycle-task-dependency-section__body">
      <div
        v-if="(listedChips || chips || []).length"
        class="cycle-task-dependency-section__chips"
        data-testid="cycle-task-dependencies__chips"
      >
        <span
          v-for="(chip, chipIndex) in (listedChips || chips || [])"
          :key="`${idString(chip.taskId)}-${chip.waitingOn ? 'waiting' : 'link'}-${chipIndex}`"
          class="cycle-task-dependency-section__chip"
          :data-testid="`cycle-task-dependencies__chip--${chipIndex}`"
        >
          <v-tooltip
            bottom
            content-class="tooltip-bottom tooltip-wrap"
            open-delay="300"
            :disabled="!isChipTruncated(chipIndex)"
          >
            <template #activator="{ on, attrs }">
              <span
                ref="chipLabels"
                class="cycle-task-dependency-section__chip-label"
                v-bind="attrs"
                v-on="on"
              >
                {{ chipDisplayLabel(chip) }}
              </span>
            </template>
            <span>{{ chipLabel(chip) }}</span>
          </v-tooltip>
          <button
            type="button"
            class="cycle-task-dependency-section__chip-remove"
            :aria-label="`Remove ${chipLabel(chip)}`"
            :data-testid="`cycle-task-dependencies__chip-remove--${chipIndex}`"
            @click="onChipRemove(chip)"
          >
            <v-icon
              size="20"
              class="cycle-task-dependency-section__chip-remove-icon"
              aria-hidden="true"
            >
              mdi-close
            </v-icon>
          </button>
        </span>
      </div>

      <div class="cycle-task-dependency-section__search">
        <v-text-field
          v-model="searchLocal"
          class="cycle-task-dependency-section__search-field"
          dense
          outlined
          hide-details
          :placeholder="copy.PICKER_SEARCH_PLACEHOLDER"
          data-testid="cycle-task-dependencies__search"
          @input="onSearchInput"
        />
        <button
          v-if="searchLocal"
          type="button"
          class="cycle-task-dependency-section__search-clear"
          aria-label="Clear search"
          data-testid="cycle-task-dependencies__search-clear"
          @click="clearSearch"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
        <img
          v-else
          src="/img/icons/fbc-icons/SearchIcon.svg"
          alt=""
          class="cycle-task-dependency-section__search-icon"
        />
      </div>

      <div
        class="cycle-task-dependency-section__table-wrap fbc-table-header-labels"
        data-testid="cycle-task-dependencies__table-wrap"
      >
        <v-progress-linear v-if="loadingInitial" indeterminate />
        <lb-datatableaction
          ref="dataTableAction"
          :headers="tableHeaders"
          :tabledata="records"
          :displayed-row-count="(pickerRecords || records || []).length"
          :itemsperpage="(pickerRecords || records || []).length || 20"
          :bulk-actions="false"
          :show-toolbar="false"
          :enable-column-reorder="false"
          :enable-cell-truncate="false"
          :page_details="{}"
          :selectfilter="{}"
          header-bg-class="fbc-table-header-bg"
          data-testid="cycle-task-dependencies__datatable"
        >
          <template #tbody="{ headers }">
            <tr
              v-for="(row, rowIndex) in (pickerRecords || records || [])"
              :key="idString(row._id) || rowIndex"
              class="cycle-task-dependency-section__row"
              :class="{
                'cycle-task-dependency-section__row--selected': isRowChecked(row),
                'cycle-task-dependency-section__row--disabled': isRowVisuallyDisabled(row),
                'cycle-task-dependency-section__row--linked-editable': isRowLinked(row) && isRowVisuallyDisabled(row),
              }"
              :aria-disabled="isRowCheckboxDisabled(row) && !isRowLinked(row) ? 'true' : 'false'"
              :data-testid="`cycle-task-dependencies__row--${rowIndex}`"
            >
              <td
                v-for="header in visibleHeaders(headers)"
                :key="`${header.value}-${rowIndex}`"
                :class="{
                  'px-4': header.value !== 'selected',
                  'cycle-task-dependency-section__cell-check checkbox-cell': header.value === 'selected',
                  'cycle-task-dependency-section__cell-details': header.value === 'taskDetails',
                  'cycle-task-dependency-section__cell-due': header.value === 'due',
                  'cycle-task-dependency-section__cell-location': header.value === 'location',
                  'cycle-task-dependency-section__cell-link': header.value === 'linkType',
                }"
                :data-testid="`cycle-task-dependencies__cell--${header.value}-${rowIndex}`"
                @click="header.value === 'selected' ? onCheckCellClick(row, $event) : undefined"
              >
                <v-checkbox
                  v-if="header.value === 'selected'"
                  dense
                  color="primary"
                  class="ma-0 cycle-task-dependency-section__checkbox"
                  :ripple="false"
                  hide-details
                  :input-value="isRowChecked(row)"
                  :disabled="isRowCheckboxDisabled(row)"
                  :tabindex="isRowCheckboxDisabled(row) ? -1 : 0"
                  :data-testid="`cycle-task-dependencies__check--${rowIndex}`"
                  @click.native.stop
                  @change="onRowToggle(row, $event)"
                />
                <div
                  v-else-if="header.value === 'taskDetails'"
                  class="cycle-task-dependency-section__task-details"
                >
                  <span
                    v-if="row.ref"
                    class="cycle-task-dependency-section__ref"
                  >{{ row.ref }}</span>
                  <v-tooltip
                    bottom
                    fixed
                    open-delay="300"
                    content-class="tooltip-bottom tooltip-wrap"
                    class="cycle-task-dependency-section__name-tooltip"
                    :disabled="descriptionLabel(row.description) === String(row.description || '').trim()"
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        class="cycle-task-dependency-section__name"
                        v-bind="attrs"
                        v-on="on"
                      >{{ descriptionLabel(row.description) }}</span>
                    </template>
                    <span>{{ row.description }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="rowDisabledTooltip(row)"
                    bottom
                    fixed
                    open-delay="200"
                    content-class="tooltip-bottom cycle-task-dependency-section__disabled-tooltip"
                  >
                    <template #activator="{ on, attrs }">
                      <button
                        type="button"
                        class="cycle-task-dependency-section__info-btn"
                        :class="{ 'cycle-task-dependency-section__info-btn--warn': rowDisabledTooltip(row).warn }"
                        v-bind="attrs"
                        :data-testid="`cycle-task-dependencies__info--${rowIndex}`"
                        v-on="on"
                      >
                        <img
                          v-if="rowDisabledTooltip(row).warn"
                          src="/img/icons/fbc-icons/AutoApprovalRejectedAlert.svg"
                          alt=""
                          class="cycle-task-dependency-section__warn-icon"
                        />
                        <v-icon v-else size="16">mdi-information-outline</v-icon>
                      </button>
                    </template>
                    <div class="cycle-task-dependency-section__disabled-tooltip-content">
                      <p class="cycle-task-dependency-section__disabled-tooltip-title">
                        {{ rowDisabledTooltip(row).title }}
                      </p>
                      <p class="cycle-task-dependency-section__disabled-tooltip-body">
                        {{ rowDisabledTooltip(row).body }}
                      </p>
                    </div>
                  </v-tooltip>
                </div>
                <template v-else-if="header.value === 'due'">
                  {{ formatDue(row.duedate) }}
                </template>
                <template v-else-if="header.value === 'location'">
                  <v-tooltip
                    bottom
                    fixed
                    open-delay="300"
                    content-class="tooltip-bottom tooltip-wrap"
                    class="cycle-task-dependency-section__location-tooltip"
                    :disabled="!row.locationName"
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        class="cycle-task-dependency-section__location"
                        v-bind="attrs"
                        v-on="on"
                      >{{ row.locationName || '—' }}</span>
                    </template>
                    <span>{{ row.locationName }}</span>
                  </v-tooltip>
                </template>
                <div
                  v-else-if="header.value === 'linkType' && isRowLinked(row) && !isActiveWaitingOn(row)"
                  class="cycle-task-dependency-section__link-type-wrap"
                >
                  <lb-dropdown
                    class="cycle-task-dependency-section__link-type"
                    :value="linkTypeForRow(row)"
                    :items="linkTypeItemsForRow(row)"
                    itemtext="label"
                    itemvalue="value"
                    :searchable="false"
                    :hidedetails="true"
                    :attach="false"
                    :flip-menu="true"
                    menu-content-class="dependency-link-type-menu"
                    appendiconinner="mdi-chevron-down"
                    label=""
                    placeholder=""
                    :data-testid="`cycle-task-dependencies__link-type--${rowIndex}`"
                    @change="onLinkTypeChange(row, $event)"
                  />
                </div>
                <span
                  v-else-if="header.value === 'linkType'"
                  class="cycle-task-dependency-section__link-empty"
                >-</span>
              </td>
            </tr>
            <tr v-if="!loading && !(pickerRecords || records || []).length">
              <td
                :colspan="visibleHeaders(headers).length || 1"
                class="cycle-task-dependency-section__table-empty"
              >
                {{ copy.PICKER_EMPTY }}
              </td>
            </tr>
          </template>
        </lb-datatableaction>
        <div
          v-if="loadingMore"
          class="cycle-task-dependency-section__loading-more"
          data-testid="cycle-task-dependencies__loading-more"
        >
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
        </div>
      </div>

      <div
        v-if="!(listedChips || chips || []).length"
        class="cycle-task-dependency-section__empty"
        data-testid="cycle-task-dependencies__empty"
      >
        <p class="cycle-task-dependency-section__empty-title">
          {{ copy.EMPTY_LINKED_TITLE }}
        </p>
        <p class="cycle-task-dependency-section__empty-body">
          {{ copy.EMPTY_BLOCKING_BODY }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import './CycleTaskDependencySection.scss';
import { mapActions } from 'vuex';
import { CYCLE_TASK_DEPENDENCY, SETUP_TEMPLATE_DEPENDENCY } from '../../../../../utils/constants';
import {
  blocksLinkPickerHint,
  collectBlocksLinkReasons,
  defaultPickerLinkType,
  dependencyChipLabel,
  truncateDependencyDescription,
  dependencySummaryLabel,
  isPickerRowInteractionDisabled,
  isPickerRowLinked,
  isSetupTemplatePickerRowDisabled,
  linkPickerDisabledTooltip,
  linkTypeItemsForPickerRow,
  mergeLinkedPickerRecords,
  rememberPickerChipDetails,
  pinWaitingOnPickerRecords,
  pickerRowId,
  sanitizeCycleEditableChips,
  templateIdString,
} from '../../../../Setup/TaskList/utils/setupDependencyUtils';

export default {
  name: 'CycleTaskDependencySection',

  props: {
    projectId: { type: String, default: '' },
    subjectDuedate: { type: String, default: '' },
    excludeTaskId: { type: String, default: '' },
    existingNeighborIds: { type: Array, default: () => [] },
    waitingOnNeighborIds: { type: Array, default: () => [] },
    waitingOnNeighbors: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
    expanded: { type: Boolean, default: false },
  },

  data() {
    return {
      copy: CYCLE_TASK_DEPENDENCY,
      linkTypeOptions: CYCLE_TASK_DEPENDENCY.LINK_TYPE_OPTIONS,
      searchLocal: '',
      searchDebounce: null,
      suppressSearchReload: false,
      loadGeneration: 0,
      loading: false,
      records: [],
      page: 1,
      totalPages: 1,
      chips: [],
      removedWaitingOnIds: [],
      scrollParent: null,
    };
  },

  computed: {
    summaryLabel() {
      const outbound = dependencySummaryLabel(this.chips);
      const waiting = this.activeWaitingOnCount;
      if (outbound && waiting > 0) return `${outbound}, ${waiting} waiting on`;
      if (outbound) return outbound;
      if (waiting > 0) return `${waiting} waiting on`;
      return '';
    },
    subjectDueReady() {
      return !!this.subjectDuedate;
    },
    locationHeader() {
      return this.$store.state.LocationHeader || this.copy.COL_LOCATION;
    },
    linkedTaskIds() {
      return new Set(
        (this.chips || [])
          .map((chip) => templateIdString(chip.taskId))
          .filter(Boolean)
      );
    },
    waitingOnNeighborIdSet() {
      const ids = (this.waitingOnNeighborIds || [])
        .map((id) => templateIdString(id))
        .filter(Boolean);
      (this.waitingOnNeighbors || []).forEach((row) => {
        const id = pickerRowId(row);
        if (id) ids.push(id);
      });
      return new Set(ids);
    },
    activeWaitingOnCount() {
      const removed = Array.isArray(this.removedWaitingOnIds) ? this.removedWaitingOnIds : [];
      let count = 0;
      this.waitingOnNeighborIdSet.forEach((id) => {
        if (!removed.includes(id)) count += 1;
      });
      return count;
    },
    listedChips() {
      const outbound = Array.isArray(this.chips) ? this.chips : [];
      const outboundIds = new Set(
        outbound.map((chip) => templateIdString(chip && chip.taskId)).filter(Boolean),
      );
      const removed = new Set(Array.isArray(this.removedWaitingOnIds) ? this.removedWaitingOnIds : []);
      const waiting = [];
      const seen = new Set();
      const neighbors = Array.isArray(this.waitingOnNeighbors) ? this.waitingOnNeighbors : [];
      neighbors.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const taskId = pickerRowId(row);
        if (!taskId || removed.has(taskId) || outboundIds.has(taskId) || seen.has(taskId)) return;
        seen.add(taskId);
        waiting.push({
          taskId,
          ref: row.ref,
          description: row.description,
          duedate: row.duedate,
          locationName: row.locationName || '',
          waitingOn: true,
        });
      });
      return waiting.concat(outbound);
    },
    pickerRecords() {
      return Array.isArray(this.records) ? this.records : [];
    },
    loadingInitial() {
      const rows = Array.isArray(this.records) ? this.records : [];
      return this.loading && !rows.length;
    },
    loadingMore() {
      const rows = Array.isArray(this.records) ? this.records : [];
      return this.loading && rows.length > 0;
    },
    tableHeaders() {
      return [
        {
          text: '',
          value: 'selected',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'cycle-task-dependency-section__col-check',
        },
        {
          text: this.copy.COL_TASK_DETAILS,
          value: 'taskDetails',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'cycle-task-dependency-section__col-details',
        },
        {
          text: this.copy.COL_DUE,
          value: 'due',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'cycle-task-dependency-section__col-due',
        },
        {
          text: this.locationHeader,
          value: 'location',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'cycle-task-dependency-section__col-location',
        },
        {
          text: this.copy.COL_LINK_TYPE,
          value: 'linkType',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'cycle-task-dependency-section__col-link',
        },
      ];
    },
  },

  watch: {
    waitingOnNeighborIds() {
      this.removedWaitingOnIds = [];
      this.$emit('update:removedWaitingOn', []);
      if (Array.isArray(this.records) && this.records.length) {
        this.records = this.mergePickerRecords(this.records);
      }
    },
    waitingOnNeighbors: {
      deep: true,
      handler() {
        if (Array.isArray(this.records) && this.records.length) {
          this.records = this.mergePickerRecords(this.records);
        }
      },
    },
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        this.chips = sanitizeCycleEditableChips(
          Array.isArray(val) ? val : [],
          this.waitingOnNeighborIdSet,
        );
      },
    },
    subjectDuedate() {
      if (!this.expanded) {
        this.keepSelectedRowDetails();
        this.records = [];
        return;
      }
      this.resetPickerAndLoad();
    },
    expanded(val) {
      if (!val) {
        this.clearSearchSilently();
        return;
      }
      this.resetPickerAndLoad();
    },
    chips: {
      deep: true,
      handler() {
        this.records = this.mergePickerRecords(this.records);
      },
    },
  },

  mounted() {
    if (this.expanded) this.resetPickerAndLoad();
    this.$nextTick(() => {
      this.bindScrollParent();
    });
  },

  beforeDestroy() {
    this.unbindScrollParent();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.records = [];
    this.chips = [];
  },

  methods: {
    ...mapActions('tasks', ['fetchTasksForLink']),

    idString: templateIdString,

    toggleExpanded() {
      this.$emit('update:expanded', !this.expanded);
    },

    chipLabel(chip) {
      return dependencyChipLabel(chip);
    },

    chipDisplayLabel(chip) {
      return dependencyChipLabel({
        ...chip,
        description: truncateDependencyDescription(chip?.description),
      });
    },

    descriptionLabel(value) {
      return truncateDependencyDescription(value);
    },

    isChipTruncated(index) {
      const chip = (this.listedChips || this.chips || [])[index];
      if (!chip) return false;
      return this.chipDisplayLabel(chip) !== this.chipLabel(chip);
    },

    rowDisabledTooltip(row) {
      if (this.isWaitingOnNeighbor(row)) return null;
      // Existing Related/Blocks chips stay quiet. Approval or due order only
      // explains why a row cannot be newly linked.
      if (this.isRowLinked(row)) return null;
      const disabledRow = linkPickerDisabledTooltip(row);
      if (disabledRow) return disabledRow;
      const blocksHint = blocksLinkPickerHint(
        row,
        SETUP_TEMPLATE_DEPENDENCY.PICKER_DISABLED_REASON_MESSAGES,
      );
      if (!blocksHint || this.isRowLinked(row)) return null;
      const code = collectBlocksLinkReasons(row)[0]?.code;
      return {
        title: 'Blocks',
        body: blocksHint,
        warn: code === 'DUE_ORDER' || code === 'MISSING_DUE',
      };
    },

    visibleHeaders(headers) {
      const source = Array.isArray(headers) && headers.length
        ? headers
        : (Array.isArray(this.tableHeaders) ? this.tableHeaders : []);
      return source.filter((header) => header && header.visible !== false);
    },

    formatDue(value) {
      if (!value) return '—';
      const formatted = this.$nova.formatDate(value, this.copy.DUE_DATE_FORMAT);
      return formatted || String(value);
    },

    emitChips() {
      this.$emit('input', sanitizeCycleEditableChips(
        this.chips,
        this.waitingOnNeighborIdSet,
      ));
    },

    mergePickerRecords(records, options = {}) {
      const merged = mergeLinkedPickerRecords(records, this.chips, {
        includeMissingChips: !String(this.searchLocal || '').trim(),
        // Checking a row keeps it in place. A due-date or search reload
        // pins already selected tasks above the new API order.
        sortSelectedFirst: options.sortSelectedFirst === true,
      });
      if (String(this.searchLocal || '').trim()) return merged;
      return pinWaitingOnPickerRecords(merged, this.waitingOnNeighbors);
    },

    resetPickerAndLoad() {
      this.keepSelectedRowDetails();
      this.page = 1;
      this.records = [];
      this.loadPage(1, true);
    },

    keepSelectedRowDetails() {
      const nextChips = rememberPickerChipDetails(this.chips, this.records);
      const changed = nextChips.some((chip, index) => chip !== this.chips[index]);
      if (!changed) return;
      this.chips = nextChips;
      this.emitChips();
    },

    clearSearchSilently() {
      this.keepSelectedRowDetails();
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      this.suppressSearchReload = true;
      this.loadGeneration += 1;
      this.loading = false;
      this.searchLocal = '';
      this.records = [];
      this.page = 1;
      this.totalPages = 1;
      this.$nextTick(() => {
        this.suppressSearchReload = false;
      });
    },

    async loadPage(pageNum, replace = false) {
      if (!this.projectId) return;
      const generation = ++this.loadGeneration;
      this.loading = true;
      try {
        const result = await this.fetchTasksForLink({
          projectId: this.projectId,
          duedate: this.subjectDueReady ? this.subjectDuedate : undefined,
          excludeTaskId: this.excludeTaskId || undefined,
          page: pageNum,
          search: this.searchLocal || '',
        });
        if (generation !== this.loadGeneration) return;
        const next = result.records || [];
        const current = Array.isArray(this.records) ? this.records : [];
        const batch = replace ? next : current.concat(next);
        this.records = this.mergePickerRecords(batch, { sortSelectedFirst: true });
        this.page = result.page || pageNum;
        this.totalPages = result.totalPages || 1;
      } catch (err) {
        if (generation !== this.loadGeneration) return;
        console.error('[CycleTaskDependencySection]:', err?.message || err);
        this.$store.commit('sbError', err?.message || 'Failed to load tasks', { root: true });
      } finally {
        if (generation === this.loadGeneration) this.loading = false;
      }
    },

    onSearchInput() {
      if (this.suppressSearchReload) return;
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.resetPickerAndLoad();
      }, 350);
    },

    clearSearch() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchLocal = '';
      this.resetPickerAndLoad();
    },

    bindScrollParent() {
      this.unbindScrollParent();
      const el = this.$el?.closest('.add-task-dialog__body');
      if (!el) return;
      this.scrollParent = el;
      el.addEventListener('scroll', this.onTableScroll, { passive: true });
    },

    unbindScrollParent() {
      if (!this.scrollParent) return;
      this.scrollParent.removeEventListener('scroll', this.onTableScroll);
      this.scrollParent = null;
    },

    onTableScroll(e) {
      const el = e?.currentTarget || e?.target || this.scrollParent;
      if (!el || this.loading || this.page >= this.totalPages) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
        this.loadPage(this.page + 1, false);
      }
    },

    chipIndexForRow(row) {
      const id = pickerRowId(row);
      return this.chips.findIndex((c) => templateIdString(c.taskId) === id);
    },

    isRowLinked(row) {
      return isPickerRowLinked(row, this.linkedTaskIds);
    },

    isWaitingOnNeighbor(row) {
      const id = pickerRowId(row);
      return !!id && this.waitingOnNeighborIdSet.has(id);
    },

    isActiveWaitingOn(row) {
      const id = pickerRowId(row);
      if (!this.isWaitingOnNeighbor(row)) return false;
      return !this.removedWaitingOnIds.includes(id);
    },

    isRowChecked(row) {
      if (this.isRowLinked(row)) return true;
      return this.isActiveWaitingOn(row);
    },

    isRowSelected(row) {
      return this.isRowChecked(row);
    },

    isRowPickerDisabled(row) {
      return isSetupTemplatePickerRowDisabled(row);
    },

    isRowVisuallyDisabled(row) {
      if (this.isWaitingOnNeighbor(row) || this.isRowLinked(row)) return false;
      return this.isRowPickerDisabled(row);
    },

    isRowCheckboxDisabled(row) {
      if (!this.subjectDueReady) return true;
      if (this.isWaitingOnNeighbor(row)) return false;
      return isPickerRowInteractionDisabled(row, this.isRowLinked(row));
    },

    onCheckCellClick(row, event) {
      if (this.isRowCheckboxDisabled(row)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target.closest('.v-input--selection-controls')) return;
      this.onRowToggle(row, !this.isRowChecked(row));
    },

    linkTypeForRow(row) {
      const idx = this.chipIndexForRow(row);
      return idx >= 0 ? this.chips[idx].linkType : '';
    },

    linkTypeItemsForRow(row) {
      const current = this.linkTypeForRow(row);
      return linkTypeItemsForPickerRow(row, this.linkTypeOptions).map((item) => (
        item.value === current ? { ...item, disabled: false } : item
      ));
    },

    onRowToggle(row, checked) {
      const id = pickerRowId(row);
      if (!id) return;
      if (this.isRowCheckboxDisabled(row)) return;
      const idx = this.chipIndexForRow(row);
      if (this.isWaitingOnNeighbor(row)) {
        this.removedWaitingOnIds = checked
          ? this.removedWaitingOnIds.filter((item) => item !== id)
          : this.removedWaitingOnIds.includes(id)
            ? this.removedWaitingOnIds
            : [...this.removedWaitingOnIds, id];
        this.$emit('update:removedWaitingOn', this.removedWaitingOnIds.slice());
        return;
      }
      if (checked) {
        if (idx >= 0) return;
        this.chips.push({
          taskId: id,
          ref: row.ref,
          description: row.description,
          duedate: row.duedate,
          locationName: row.locationName || '',
          linkType: defaultPickerLinkType(row),
        });
      } else if (idx >= 0) {
        this.chips.splice(idx, 1);
      }
      this.emitChips();
    },

    onLinkTypeChange(row, linkType) {
      if (!linkType || this.isActiveWaitingOn(row)) return;
      const idx = this.chipIndexForRow(row);
      if (idx < 0) return;
      this.$set(this.chips[idx], 'linkType', linkType);
      if (row.duedate != null && row.duedate !== '') {
        this.$set(this.chips[idx], 'duedate', row.duedate);
      }
      this.emitChips();
    },

    onChipRemove(chip) {
      const id = templateIdString(chip?.taskId);
      if (!id) return;
      if (chip?.waitingOn) {
        if (!this.removedWaitingOnIds.includes(id)) {
          this.removedWaitingOnIds = [...this.removedWaitingOnIds, id];
          this.$emit('update:removedWaitingOn', this.removedWaitingOnIds.slice());
        }
        return;
      }
      const idx = this.chips.findIndex((c) => templateIdString(c.taskId) === id);
      if (idx < 0) return;
      this.chips.splice(idx, 1);
      this.emitChips();
    },
  },
};
</script>
